import { Box, MenuItem, SvgIconTypeMap, Typography } from "@material-ui/core";

import LoadingIndicator from "~/components/loadingIndicator/loadingIndicator";
import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import React from "react";
import ToolbarSelect from "~/components/select/toolbarSelect";
import styled from "styled-components";

const CustomMenuItem = styled(MenuItem)`
  width: ${({ width }: { width?: number | null }) => width}px;
`;

const SvgBox = styled(Box)`
  & > svg {
    width: 1rem;
    height: 1rem;
  }
`;

export interface Option {
  name: string;
  label: string;
}

interface GenericSelectProps {
  id: string;
  title: string;
  tooltip?: string;
  currentOption?: Option;
  setOption: (reportType: Option) => void;
  options: Option[];
  placeholderLabel?: string;
  isLoading?: boolean;
  disabled?: boolean;
  icon?: {
    SvgComponent: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
    align?: "start" | "end";
  };
}

export const GenericSelect: React.FC<GenericSelectProps> = ({
  id,
  title,
  tooltip,
  currentOption,
  setOption,
  options,
  placeholderLabel,
  isLoading,
  disabled,
  icon,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [menuWidth, setMenuWidth] = React.useState(
    Boolean(anchorEl) ? anchorEl?.offsetWidth : null
  );

  const changeOption = (reportType: Option) => {
    setAnchorEl(null);
    setOption(reportType);
  };

  const renderOptions = () => {
    if (isLoading) {
      return (
        <CustomMenuItem key={`loading`} width={menuWidth}>
          <LoadingIndicator />
        </CustomMenuItem>
      );
    }

    return options.map((option) => {
      return (
        <CustomMenuItem
          key={option.name}
          width={menuWidth}
          onClick={() => changeOption(option)}
        >
          <Typography>{option.label}</Typography>
        </CustomMenuItem>
      );
    });
  };

  return (
    <ToolbarSelect
      id={id}
      title={title}
      tooltip={tooltip}
      renderOptions={renderOptions}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      setMenuWidth={setMenuWidth}
      fullWidth
      alignItems="center"
      disabled={disabled}
      displayComponent={
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          flexWrap="nowrap"
          flexDirection={icon?.align === "end" ? "row-reverse" : "row"}
        >
          {icon?.SvgComponent && (
            <SvgBox
              display="flex"
              alignItems="center"
              ml={icon?.align === "end" ? 1 : 0}
              mr={icon?.align === "end" ? 0 : 1}
              flexShrink={0}
            >
              <icon.SvgComponent fill="currentColor" />
            </SvgBox>
          )}
          <Box flexGrow={1}>
            <Typography variant="body2" color="textPrimary" align="left">
              {currentOption ? currentOption.label : placeholderLabel ?? title}
            </Typography>
          </Box>
        </Box>
      }
    />
  );
};
